import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { type PartnerSelectEntity } from '@/libs/api'
import { type PartnersSelectQuery } from '@/libs/api/types'

import { fetchPartnersSelect } from '../api'
import { partnersQueryKeys } from '../query-keys'

/**
 * パートナー検索用のUseQuery（デバウンス対応）
 */
export const usePartnersSearchQuery = (
  tenantKey: string,
  query: Omit<NonNullable<PartnersSelectQuery>, 'enable'>,
  options?: {
    disabled?: boolean
    debounceDelay?: number
  }
): UseQueryResult<PartnerSelectEntity[]> => {
  const searchTerm = query.name
  const disabled = options?.disabled ?? false
  const debounceDelay = options?.debounceDelay ?? 300

  // 検索語句をデバウンス
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, debounceDelay ?? 300)

    return () => {
      clearTimeout(timer)
    }
  }, [searchTerm, debounceDelay])

  return useQuery<PartnerSelectEntity[]>({
    queryKey: [
      ...partnersQueryKeys.all,
      'search',
      tenantKey,
      debouncedSearchTerm,
      query
    ],
    queryFn: async ({ signal }) => {
      const { data } = await fetchPartnersSelect(
        tenantKey,
        {
          ...query,
          enable: true
        },
        signal
      )
      return data
    },
    enabled: !disabled,
    staleTime: 1000 * 60 * 5, // 5分間キャッシュ
    retry: false
  })
}
